import { useState } from "react";
import { Check, Copy, Loader2, Mail, Send, Users } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { AJUDA_EQUIPE_E_PARCEIROS } from "@/lib/ajuda/dialogos/equipe-e-parceiros";
import { env } from "@/lib/env";
import { cn } from "@/lib/cn";
import { resolveDashboardMode } from "@/lib/dashboard-mode";
import { DialogField } from "@/components/shared/InviteResultStage";
import { ROLE_LABEL, getRoleLabel, papeisAtribuiveis, type InviteRole } from "./role-meta";
import { ConviteEmLoteForm } from "./ConviteEmLoteForm";
import { OutrasOrganizacoesDoConvite, type OrganizacaoDoConvite } from "./OutrasOrganizacoesDoConvite";
import { ehDonoOuSocio } from "@bookdental/schemas";

type Props = {
  open: boolean;
  onClose: () => void;
};

type RoleChoice = { kind: "base"; role: InviteRole } | { kind: "custom"; customRoleId: string; label: string };

type Stage =
  | { kind: "form"; email: string; choice: RoleChoice }
  | { kind: "lote"; choice: RoleChoice }
  | { kind: "result"; email: string; roleLabel: string; token: string };

const DEFAULT_CHOICE: RoleChoice = { kind: "base", role: "DENTIST" };

export function TeamInviteDialog({ open, onClose }: Props) {
  const [stage, setStage] = useState<Stage>({ kind: "form", email: "", choice: DEFAULT_CHOICE });
  const [copied, setCopied] = useState(false);
  const [outras, setOutras] = useState<OrganizacaoDoConvite[]>([]);

  const utils = trpc.useUtils();
  const meQuery = trpc.me.current.useQuery();
  const viewerIsOwner = ehDonoOuSocio(meQuery.data?.activeOrganization?.role);
  const souProprietario = meQuery.data?.activeOrganization?.role === "OWNER";
  const mode = resolveDashboardMode(meQuery.data?.activeOrganization?.types ?? []);
  const customRolesQuery = trpc.organization.customRoles.list.useQuery(undefined, {
    enabled: viewerIsOwner,
  });
  const customRoles = customRolesQuery.data ?? [];

  const createInvite = trpc.team.invite.create.useMutation({
    onSuccess: (invite) => {
      setStage({ kind: "result", email: invite.email, roleLabel: ROLE_LABEL[invite.role as InviteRole], token: invite.token });
      utils.team.invite.list.invalidate();
    },
    onError: (err) => toast.error(err.message || "Erro ao enviar convite."),
  });
  const createInviteForCustomRole = trpc.team.invite.createForCustomRole.useMutation({
    onSuccess: (invite, variables) => {
      const label = customRoles.find((cr: any) => cr.id === variables.customRoleId)?.label ?? "Papel customizado";
      setStage({ kind: "result", email: invite.email, roleLabel: label, token: invite.token });
      utils.team.invite.list.invalidate();
    },
    onError: (err) => toast.error(err.message || "Erro ao enviar convite."),
  });
  const createEmVarias = trpc.team.invite.createEmVarias.useMutation({
    onSuccess: (r) => {
      utils.team.invite.invalidate();
      if (r.pulados.length > 0) {
        toast.info(r.pulados.map((x) => `${x.name}: ${x.motivo}`).join(" · "));
      }
      if (r.criados.length === 0) return;
      setStage({
        kind: "result",
        email: stage.kind === "form" ? stage.email.trim() : "",
        roleLabel: `${r.criados.length} organizações (${r.criados.map((c) => c.name).join(", ")})`,
        token: r.criados[0].token,
      });
    },
    onError: (err) => toast.error(err.message || "Não foi possível enviar o convite."),
  });
  const isPending = createInvite.isPending || createInviteForCustomRole.isPending || createEmVarias.isPending;

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setStage({ kind: "form", email: "", choice: DEFAULT_CHOICE });
      setCopied(false);
      setOutras([]);
    }, 250);
  };

  const inviteUrl = stage.kind === "result" ? `${env.landingUrl}/equipe/${stage.token}` : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      toast.success("Link copiado.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Não foi possível copiar.");
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (stage.kind !== "form") return;
    const email = stage.email.trim();
    // Com outras organizações, vai num convite de grupo: um e-mail só, entra em todas ao aceitar.
    if (outras.length > 0 && meQuery.data?.activeOrganization?.id) {
      const daAtiva =
        stage.choice.kind === "custom" ? { customRoleId: stage.choice.customRoleId } : { role: stage.choice.role };
      createEmVarias.mutate({
        email,
        organizacoes: [
          { organizationId: meQuery.data.activeOrganization.id, ...daAtiva },
          ...outras.map((o) => ({ organizationId: o.organizationId, role: o.role })),
        ],
      });
      return;
    }
    if (stage.choice.kind === "custom") {
      createInviteForCustomRole.mutate({ email, customRoleId: stage.choice.customRoleId, expiresInDays: 7 });
    } else {
      createInvite.mutate({ email, role: stage.choice.role, expiresInDays: 7 });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-md dark:border-border dark:bg-card">
        <DialogHeader>
          <DialogTitle
            className="dark:text-foreground"
            ajuda={
              stage.kind === "form"
                ? AJUDA_EQUIPE_E_PARCEIROS.convidarMembro
                : stage.kind === "lote"
                  ? AJUDA_EQUIPE_E_PARCEIROS.convidarVarios
                  : AJUDA_EQUIPE_E_PARCEIROS.conviteDeMembroEnviado
            }
          >
            {stage.kind === "form"
              ? "Convidar membro"
              : stage.kind === "lote"
                ? "Convidar mais de um"
                : "Convite enviado"}
          </DialogTitle>
          <DialogDescription className="dark:text-muted-foreground">
            {stage.kind === "form"
              ? "A pessoa recebe um email com o link. Se já tiver conta, aceita em 1 clique."
              : stage.kind === "lote"
                ? "Cole a lista de e-mails. Quem já está na equipe é pulado, sem receber nada."
                : "O email já foi enviado — o link abaixo é só um backup."}
          </DialogDescription>
        </DialogHeader>

        {stage.kind === "lote" && (
          <ConviteEmLoteForm
            papel={
              stage.choice.kind === "custom"
                ? { kind: "custom", customRoleId: stage.choice.customRoleId, label: stage.choice.label }
                : { kind: "base", role: stage.choice.role }
            }
            rotuloDoPapel={
              stage.choice.kind === "custom" ? stage.choice.label : getRoleLabel(stage.choice.role, mode)
            }
            onFechar={() => setStage({ kind: "form", email: "", choice: stage.choice })}
          />
        )}

        {stage.kind === "form" && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <DialogField icon={Mail} label="Email">
              <input
                type="email"
                required
                autoFocus
                name="bd-team-invite-email"
                autoComplete="off"
                data-1p-ignore
                data-lpignore
                value={stage.email}
                onChange={(e) => setStage({ ...stage, email: e.target.value })}
                placeholder="colega@exemplo.com"
                className="w-full bg-transparent text-sm text-zinc-900 outline-none placeholder:text-zinc-400 dark:text-foreground dark:placeholder:text-muted-foreground"
              />
            </DialogField>

            <div>
              <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">
                Cargo
              </label>
              <div className="grid grid-cols-2 gap-1.5">
                {papeisAtribuiveis(mode, null, souProprietario).map((r) => {
                  const active = stage.choice.kind === "base" && stage.choice.role === r;
                  return (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setStage({ ...stage, choice: { kind: "base", role: r } })}
                      className={cn(
                        "rounded-lg border px-2.5 py-2 text-[12.5px] font-semibold transition-all",
                        active
                          ? "border-brand-500 bg-brand-50 text-brand-900 dark:border-primary dark:bg-primary/10 dark:text-primary"
                          : "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300 dark:border-border dark:bg-background dark:text-foreground/80",
                      )}
                    >
                      {getRoleLabel(r, mode)}
                    </button>
                  );
                })}
              </div>
              {customRoles.length > 0 && (
                <div className="mt-1.5 grid grid-cols-2 gap-1.5">
                  {customRoles.map((cr: any) => {
                    const active = stage.choice.kind === "custom" && stage.choice.customRoleId === cr.id;
                    return (
                      <button
                        key={cr.id}
                        type="button"
                        onClick={() =>
                          setStage({ ...stage, choice: { kind: "custom", customRoleId: cr.id, label: cr.label } })
                        }
                        className={cn(
                          "rounded-lg border px-2.5 py-2 text-[12.5px] font-semibold transition-all",
                          active
                            ? "border-brand-500 bg-brand-50 text-brand-900 dark:border-primary dark:bg-primary/10 dark:text-primary"
                            : "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300 dark:border-border dark:bg-background dark:text-foreground/80",
                        )}
                      >
                        {cr.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <OutrasOrganizacoesDoConvite valor={outras} onChange={setOutras} />

            {/* No celular os dois dividem a largura, com a ação principal maior: Cancelar era um
                botão minúsculo ao lado de um grande. Proporcional em vez de igual pra hierarquia
                não sumir — botões idênticos disputam a atenção. */}
            {/* Atalho, não segunda aba: convidar uma pessoa é o caso comum e continua sendo o que a
                tela abre. O cargo já escolhido acima vai junto pro lote — voltar e reescolher seria
                refazer o que a pessoa acabou de fazer. */}
            <button
              type="button"
              onClick={() => setStage({ kind: "lote", choice: stage.choice })}
              className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-brand-700 transition-colors hover:text-brand-800 dark:text-primary"
            >
              <Users className="h-3.5 w-3.5" />
              Convidar mais de um
            </button>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={handleClose}
                className="inline-flex h-10 flex-1 items-center justify-center rounded-md border border-zinc-200 px-3 text-[13px] font-medium text-zinc-700 transition-colors hover:bg-zinc-50 sm:h-9 sm:flex-none dark:border-border dark:text-foreground dark:hover:bg-muted"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isPending || !stage.email.trim()}
                className="inline-flex h-10 flex-[1.4] items-center justify-center gap-1.5 rounded-md bg-brand-600 px-4 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 disabled:opacity-60 sm:h-9 sm:flex-none"
              >
                {isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Send className="h-3.5 w-3.5" />
                )}
                Enviar convite
              </button>
            </div>
          </form>
        )}

        {stage.kind === "result" && (
          <div className="space-y-4">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-[12.5px] text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300">
              Email enviado para <strong>{stage.email}</strong> como{" "}
              <strong>{stage.roleLabel}</strong>.
            </div>

            <div className="rounded-lg bg-gradient-to-br from-brand-50 to-brand-100/50 p-3 text-center dark:from-primary/15 dark:to-primary/5">
              <div className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-brand-700 dark:text-primary">
                Link do convite (backup)
              </div>
              <div className="select-all break-all font-mono text-[12.5px] text-zinc-800 dark:text-foreground">
                {inviteUrl}
              </div>
            </div>

            <button
              type="button"
              onClick={copy}
              className="inline-flex h-10 w-full items-center justify-center gap-1.5 rounded-md border border-zinc-200 bg-white text-[12.5px] font-semibold text-zinc-700 transition-colors hover:bg-zinc-50 dark:border-border dark:bg-background dark:text-foreground dark:hover:bg-muted"
            >
              {copied ? (
                <>
                  <Check className="h-3.5 w-3.5 text-emerald-600" />
                  Copiado
                </>
              ) : (
                <>
                  <Copy className="h-3.5 w-3.5" />
                  Copiar link
                </>
              )}
            </button>

            <div className="flex justify-end gap-2 border-t border-zinc-100 pt-3 dark:border-border">
              <button
                type="button"
                onClick={() => setStage({ kind: "form", email: "", choice: DEFAULT_CHOICE })}
                className="inline-flex h-9 items-center rounded-md border border-zinc-200 px-3 text-[13px] font-medium text-zinc-700 transition-colors hover:bg-zinc-50 dark:border-border dark:text-foreground dark:hover:bg-muted"
              >
                Convidar outro
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="inline-flex h-9 items-center rounded-md bg-brand-600 px-4 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-700"
              >
                Fechar
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
